import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  MessageSquare, X, Send, Sparkles, HelpCircle, ShieldCheck, 
  ExternalLink, Mail, ArrowRight, Bot, User, CheckCircle2, ChevronDown
} from 'lucide-react';
import BrandLogo from './BrandLogo';

interface FloatingSupportChatWidgetProps {
  onNavigateToPath?: (path: string) => void;
  onLaunchWorkspace?: (url?: string) => void;
}

interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  quickActions?: { label: string; action: () => void }[];
}

export default function FloatingSupportChatWidget({
  onNavigateToPath,
  onLaunchWorkspace
}: FloatingSupportChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome-1',
      sender: 'assistant',
      text: '👋 Hi there! I am Zipytiny AI Support Assistant. How can I help you study, convert videos into notes, or get started today?',
      timestamp: 'Just now'
    }
  ]);

  const quickPrompts = [
    {
      label: "How do I turn a video into Anki flashcards? 🗂️",
      query: "How do I turn a video into Anki flashcards?"
    },
    {
      label: "What video formats and limits are supported? ⏱️",
      query: "What video formats and upload limits are supported?"
    },
    {
      label: "How does Zipytiny compare to Quizlet & NoteGPT? ⚡",
      query: "How does Zipytiny compare to Quizlet and NoteGPT?"
    },
    {
      label: "Can I use it for Zoom or Teams recordings? 👥",
      query: "Can I use it for Zoom or Teams recordings?"
    }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      setUnreadCount(0);
    }
  }, [isOpen, messages]);

  const generateAIResponse = (userQuery: string): { reply: string; quickActions?: { label: string; action: () => void }[] } => {
    const q = userQuery.toLowerCase();

    if (q.includes('anki') || q.includes('flashcard')) {
      return {
        reply: "To generate Anki flashcards: simply paste any YouTube lecture or upload your notes in Zipytiny. Our AI extracts active recall question-answer pairs. Once loaded, click 'Export' -> 'Anki TSV Deck' to download a ready-to-import deck file for Anki Desktop or Mobile!",
        quickActions: [
          {
            label: "Open AI Flashcards Generator →",
            action: () => {
              setIsOpen(false);
              onNavigateToPath ? onNavigateToPath('/ai-flashcards/') : (window.location.href = '/ai-flashcards/');
            }
          }
        ]
      };
    }

    if (q.includes('format') || q.includes('limit') || q.includes('length') || q.includes('size')) {
      return {
        reply: "Zipytiny supports: \n• YouTube URLs of any length (up to 3+ hours per lecture)\n• PDF, DOCX, PPTX slides, and TXT transcripts\n• MP3, WAV, M4A, MP4 audio/video uploads (up to 25MB on free tier, up to 500MB on Pro)\n• Native multilingual support for English, Arabic, Spanish, French, and more.",
        quickActions: [
          {
            label: "Explore Supported Features →",
            action: () => {
              setIsOpen(false);
              onNavigateToPath ? onNavigateToPath('/features/') : (window.location.href = '/features/');
            }
          }
        ]
      };
    }

    if (q.includes('quizlet') || q.includes('notegpt') || q.includes('different') || q.includes('versus') || q.includes('compare')) {
      return {
        reply: "Unlike Quizlet (which requires manual card typing or paywalled uploads) and NoteGPT (which gives only flat text summaries), Zipytiny generates an entire active study ecosystem: structured Cornell notes, interactive 10-question diagnostic quizzes with grade rationales, clickable concept mind maps, and 1-click Anki export!",
        quickActions: [
          {
            label: "Compare Zipytiny vs Quizlet →",
            action: () => {
              setIsOpen(false);
              onNavigateToPath ? onNavigateToPath('/compare/zipytiny-vs-quizlet/') : (window.location.href = '/compare/zipytiny-vs-quizlet/');
            }
          },
          {
            label: "Compare vs NoteGPT →",
            action: () => {
              setIsOpen(false);
              onNavigateToPath ? onNavigateToPath('/compare/zipytiny-vs-notegpt/') : (window.location.href = '/compare/zipytiny-vs-notegpt/');
            }
          }
        ]
      };
    }

    if (q.includes('zoom') || q.includes('teams') || q.includes('meeting') || q.includes('gmeet')) {
      return {
        reply: "Yes! For Zoom, Google Meet, or Microsoft Teams calls, you can upload the recording audio/video or export the meeting transcript (.vtt, .txt, .mp4). Zipytiny will convert it into an executive meeting recap, action items list with owners, and bulleted takeaways.",
        quickActions: [
          {
            label: "Client Meeting Recaps →",
            action: () => {
              setIsOpen(false);
              onNavigateToPath ? onNavigateToPath('/use-cases/client-meeting-recap/') : (window.location.href = '/use-cases/client-meeting-recap/');
            }
          }
        ]
      };
    }

    if (q.includes('price') || q.includes('cost') || q.includes('free') || q.includes('plan') || q.includes('upgrade')) {
      return {
        reply: "Zipytiny offers a generous Free Forever tier with instant workspace generations, public study library access, and no credit card required. Pro plans start at $9.99/mo for unlimited high-speed parsing, 4K audio transcription, and batch exports.",
        quickActions: [
          {
            label: "View Pricing Plans →",
            action: () => {
              setIsOpen(false);
              onNavigateToPath ? onNavigateToPath('/pricing/') : (window.location.href = '/pricing/');
            }
          }
        ]
      };
    }

    if (q.includes('human') || q.includes('contact') || q.includes('email') || q.includes('founder') || q.includes('bug') || q.includes('issue')) {
      return {
        reply: "You can reach our engineering & support team anytime at support@zipytiny.app. We typically respond within a few hours. For urgent billing or enterprise requests, email hello@zipytiny.app.",
        quickActions: [
          {
            label: "Email Support Desk →",
            action: () => {
              window.location.href = "mailto:support@zipytiny.app?subject=Support%20Request%20-%20Zipytiny";
            }
          }
        ]
      };
    }

    return {
      reply: "Thanks for asking! Zipytiny makes learning from long video content 10x faster by turning lectures into Cornell notes, active recall flashcards, and diagnostic tests. Would you like to try it with a sample lecture or upload your own file?",
      quickActions: [
        {
          label: "Open Workspace Demo →",
          action: () => {
            setIsOpen(false);
            if (onLaunchWorkspace) onLaunchWorkspace();
          }
        },
        {
          label: "Browse Study Library →",
          action: () => {
            setIsOpen(false);
            onNavigateToPath ? onNavigateToPath('/study/') : (window.location.href = '/study/');
          }
        }
      ]
    };
  };

  const handleSendMessage = (textToSend?: string) => {
    const text = (textToSend || inputText).trim();
    if (!text) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text,
      timestamp: 'Just now'
    };

    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsTyping(true);

    setTimeout(() => {
      const { reply, quickActions } = generateAIResponse(text);
      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        sender: 'assistant',
        text: reply,
        timestamp: 'Just now',
        quickActions
      };
      setMessages(prev => [...prev, assistantMsg]);
      setIsTyping(false);
    }, 450);
  };

  return (
    <aside aria-label="Support and Help Center" className="fixed bottom-5 right-5 z-50 font-sans print:hidden">
      {/* Floating Trigger Button */}
      {!isOpen && (
        <motion.button
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white px-4 py-3 rounded-full shadow-xl shadow-indigo-500/25 border border-indigo-400/30 cursor-pointer group transition-all"
          aria-label="Open AI Support Chat"
        >
          <div className="relative">
            <MessageSquare className="w-5 h-5 text-white" />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 border-2 border-indigo-600 rounded-full animate-ping"></span>
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 border-2 border-indigo-600 rounded-full"></span>
          </div>
          <span className="text-xs font-extrabold tracking-tight hidden sm:inline">
            Support &amp; Help
          </span>
          <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full font-mono font-bold hidden sm:inline">
            24/7 AI
          </span>
        </motion.button>
      )}

      {/* Support Chat Box Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="w-[92vw] sm:w-96 max-h-[580px] h-[520px] bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-3xl shadow-2xl flex flex-col overflow-hidden text-left"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white p-4 flex items-center justify-between shrink-0 shadow-sm">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-xs">
                  <BrandLogo size={22} />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-bold text-sm tracking-tight text-white">Zipytiny Support</h3>
                    <span className="flex h-2 w-2 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                    </span>
                  </div>
                  <p className="text-[10px] text-indigo-100 font-medium">Instant answers &amp; help</p>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-xl hover:bg-white/20 text-white transition cursor-pointer"
                  aria-label="Close support chat"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Quick Prompt Chips (Sticky Top) */}
            <div className="p-2.5 bg-slate-50 dark:bg-zinc-850 border-b border-slate-100 dark:border-zinc-800 flex gap-1.5 overflow-x-auto no-scrollbar shrink-0">
              {quickPrompts.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSendMessage(item.query)}
                  className="text-[11px] font-semibold bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 text-slate-700 dark:text-zinc-300 px-2.5 py-1 rounded-xl whitespace-nowrap hover:border-indigo-500 dark:hover:border-indigo-400 hover:text-indigo-600 transition cursor-pointer shrink-0"
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Message Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-slate-50/50 dark:bg-zinc-950/50 text-xs">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl leading-relaxed whitespace-pre-wrap ${
                      msg.sender === 'user'
                        ? 'bg-indigo-600 text-white rounded-br-xs'
                        : 'bg-white dark:bg-zinc-800 text-slate-800 dark:text-zinc-100 border border-slate-200 dark:border-zinc-700 rounded-bl-xs shadow-xs'
                    }`}
                  >
                    {msg.text}

                    {msg.quickActions && msg.quickActions.length > 0 && (
                      <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-zinc-700 flex flex-col gap-1.5">
                        {msg.quickActions.map((act, aIdx) => (
                          <button
                            key={aIdx}
                            onClick={act.action}
                            className="text-left font-bold text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 cursor-pointer"
                          >
                            <span>{act.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-[9px] text-slate-400 mt-1 px-1">{msg.timestamp}</span>
                </div>
              ))}

              {isTyping && (
                <div className="flex items-center gap-1.5 text-slate-400 text-xs p-2 bg-white dark:bg-zinc-800 rounded-2xl w-fit border border-slate-200 dark:border-zinc-700">
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></span>
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                  <span className="text-[10px] ml-1 text-slate-500 font-medium">Zipytiny AI is typing...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Direct Email Support Bar */}
            <div className="px-3 py-1.5 bg-slate-100 dark:bg-zinc-850 border-t border-slate-200 dark:border-zinc-800 flex items-center justify-between text-[10px] text-slate-500 dark:text-zinc-400 shrink-0">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3 text-indigo-500" />
                <span>support@zipytiny.app</span>
              </span>
              <a
                href="mailto:support@zipytiny.app"
                className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline"
              >
                Send Email
              </a>
            </div>

            {/* Input Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="p-2.5 bg-white dark:bg-zinc-900 border-t border-slate-200 dark:border-zinc-800 flex items-center gap-2 shrink-0"
            >
              <input
                type="text"
                placeholder="Ask anything about Zipytiny..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="flex-1 bg-slate-100 dark:bg-zinc-800 border-none rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <button
                type="submit"
                disabled={!inputText.trim()}
                className="p-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white transition cursor-pointer"
                aria-label="Send support message"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </aside>
  );
}
